import React, { useState, useEffect } from 'react';
import { Accessibility, ZoomIn, ZoomOut, Sun, Moon, Type, Eye, RotateCcw, X } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';

export default function AccessibilityWidget() {
  const { t } = useLanguage();
  const [open, setOpen] = useState(false);
  const [fontSize, setFontSize] = useState(100);
  const [highContrast, setHighContrast] = useState(false);
  const [largeText, setLargeText] = useState(false);

  useEffect(() => {
    const handler = () => setOpen(prev => !prev);
    window.addEventListener('toggle-accessibility', handler);
    return () => window.removeEventListener('toggle-accessibility', handler);
  }, []);

  useEffect(() => {
    document.documentElement.style.fontSize = `${fontSize}%`;
  }, [fontSize]);

  useEffect(() => {
    if (highContrast) {
      document.documentElement.classList.add('high-contrast');
    } else {
      document.documentElement.classList.remove('high-contrast');
    }
  }, [highContrast]);

  const reset = () => {
    setFontSize(100);
    setHighContrast(false);
    setLargeText(false);
    document.documentElement.style.fontSize = '100%';
    document.documentElement.classList.remove('high-contrast');
  };

  return (
    <>
      {/* Toggle Button */}
      <button
        onClick={() => setOpen(!open)}
        data-testid="accessibility-toggle"
        className="fixed bottom-6 left-6 z-50 w-12 h-12 bg-navy dark:bg-white/10 text-white rounded-full shadow-lg flex items-center justify-center hover:scale-105 transition-transform"
        aria-label="Accessibility tools"
      >
        <Accessibility size={20} />
      </button>

      {/* Panel */}
      {open && (
        <div className="fixed bottom-20 left-6 z-50 w-[240px] bg-white dark:bg-gray-900 rounded-xl shadow-2xl border border-gray-200 dark:border-gray-700 p-4 animate-fade-in" data-testid="accessibility-panel">
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-display text-sm font-bold text-navy dark:text-white flex items-center gap-1.5">
              <Accessibility size={14} className="text-sky-500" /> Accessibility
            </h3>
            <button onClick={() => setOpen(false)} className="p-1 hover:bg-gray-100 dark:hover:bg-gray-800 rounded" data-testid="accessibility-close">
              <X size={14} className="text-gray-400" />
            </button>
          </div>

          <div className="space-y-3">
            {/* Font Size */}
            <div>
              <label className="text-[10px] text-gray-400 font-display uppercase tracking-wider mb-1 block">Font Size ({fontSize}%)</label>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setFontSize(prev => Math.max(80, prev - 10))}
                  className="p-1.5 bg-gray-100 dark:bg-gray-800 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors"
                  data-testid="font-decrease"
                >
                  <ZoomOut size={14} className="text-gray-600 dark:text-gray-300" />
                </button>
                <div className="flex-1 h-1.5 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
                  <div className="h-full bg-sky-500 rounded-full transition-all" style={{ width: `${((fontSize - 80) / 60) * 100}%` }} />
                </div>
                <button
                  onClick={() => setFontSize(prev => Math.min(140, prev + 10))}
                  className="p-1.5 bg-gray-100 dark:bg-gray-800 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors"
                  data-testid="font-increase"
                >
                  <ZoomIn size={14} className="text-gray-600 dark:text-gray-300" />
                </button>
              </div>
            </div>

            {/* High Contrast */}
            <button
              onClick={() => setHighContrast(!highContrast)}
              data-testid="high-contrast-toggle"
              className={`w-full flex items-center gap-2 px-3 py-2 rounded-lg text-xs font-display transition-colors ${
                highContrast ? 'bg-sky-500 text-white' : 'bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-700'
              }`}
            >
              <Eye size={14} />
              High Contrast
            </button>

            {/* Large Text */}
            <button
              onClick={() => {
                setLargeText(!largeText);
                setFontSize(largeText ? 100 : 120);
              }}
              data-testid="large-text-toggle"
              className={`w-full flex items-center gap-2 px-3 py-2 rounded-lg text-xs font-display transition-colors ${
                largeText ? 'bg-sky-500 text-white' : 'bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-700'
              }`}
            >
              <Type size={14} />
              Large Text
            </button>

            {/* Reset */}
            <button
              onClick={reset}
              data-testid="accessibility-reset"
              className="w-full flex items-center gap-2 px-3 py-2 rounded-lg text-xs font-display bg-red-50 dark:bg-red-500/10 text-red-500 hover:bg-red-100 dark:hover:bg-red-500/20 transition-colors"
            >
              <RotateCcw size={14} />
              Reset All
            </button>
          </div>
        </div>
      )}
    </>
  );
}
